import router from "./routerInstance";

interface RouteHistoryState {
    owaRouteHistoryIndex?: number;
    owaRouteHistoryLatestIndex?: number;
}

let historyNavigationPending = false;
let historyListenerRegistered = false;
let latestRouteHistoryIndex = 0;

export function initializeRouteHistory(): void {
    if (!historyListenerRegistered) {
        window.addEventListener("popstate", onHistoryChanged);
        historyListenerRegistered = true;
    }
    
    const index = ensureRouteHistoryIndex();
    latestRouteHistoryIndex = Math.max(latestRouteHistoryIndex, index, getRouteHistoryIndex("owaRouteHistoryLatestIndex") ?? index);
    setRouteHistoryIndex(index);
}

export function setRouteWithHistory(route: string): void {
    const index = ensureRouteHistoryIndex();
    router.setRoute(route);
    latestRouteHistoryIndex = index + 1;
    setRouteHistoryIndex(index + 1);
    historyNavigationPending = false;
}

export function navigateToPreviousRoute(): void {
    const index = getRouteHistoryIndex();
    // Do not traverse untracked history, including sign-in pages and flows that clear state.
    if (index !== undefined && index > 0 && !historyNavigationPending) {
        historyNavigationPending = true;
        window.history.back();
    }
}

export function navigateToNextRoute(): void {
    const index = getRouteHistoryIndex();
    // Only advance through the tracked history segment, not arbitrary browser history.
    if (index !== undefined && index < latestRouteHistoryIndex && !historyNavigationPending) {
        historyNavigationPending = true;
        window.history.forward();
    }
}

function onHistoryChanged(): void {
    historyNavigationPending = false;
    const index = getRouteHistoryIndex();
    latestRouteHistoryIndex =
        index === undefined
            ? 0
            : Math.max(latestRouteHistoryIndex ||
                getRouteHistoryIndex("owaRouteHistoryLatestIndex") ||
                0, index);
    if (index !== undefined) {
        // Overwrite stale boundaries left on entries before a forward branch was discarded.
        setRouteHistoryIndex(index);
    }
}

function ensureRouteHistoryIndex(): number {
    const index = getRouteHistoryIndex();
    if (index !== undefined) {
        return index;
    }
    
    latestRouteHistoryIndex = 0;
    setRouteHistoryIndex(0);
    return 0;
}

function setRouteHistoryIndex(index: number): void {
    window.history.replaceState({
        ...window.history.state,
        owaRouteHistoryIndex: index,
        owaRouteHistoryLatestIndex: latestRouteHistoryIndex,
    }, document.title);
}

function getRouteHistoryIndex(key: keyof RouteHistoryState = "owaRouteHistoryIndex"): number | undefined {
    const state: RouteHistoryState | null = window.history.state;
    const index = state?.[key];
    return typeof index === "number" && Number.isSafeInteger(index) && index >= 0
        ? index
        : undefined;
}
